import React, { useEffect } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import DropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import InputBox from "src/business-types/grocery/components/InputBox";
import { useConfigurationState } from "./hooks/useConfigurationState";
import { dropDownValue, billNoDropDownValue } from "./types/configuration.types";
import { PageTitle } from "../PageTitle";

const TableBillingTab = () => {
  const {
    user,
    tableRows,
    setTableRows,
    billNoEnabled,
    setBillNoEnabled,
    billNo,
    setBillNo,
    deliveryFeeEnabled,
    setDeliveryFeeEnabled,
    parcelFeeEnabled,
    setParcelFeeEnabled,
    tipsEnabled,
    setTipsEnabled,
    orderIdEnabled,
    setOrderIdEnabled,
    businessName,
    setBusinessName,
    saveTableBilling,
    productEditEnabled,
    setProductEditEnabled,
  } = useConfigurationState();

  const [initialState, setInitialState] = React.useState({
    tableRows,
    billNoEnabled,
    billNo,
    deliveryFeeEnabled,
    orderIdEnabled,
    businessName,
    parcelFeeEnabled,
    tipsEnabled,
    productEditEnabled,
  });

  // Set orderIdEnabled to true by default when component mounts
  useEffect(() => {
    if (orderIdEnabled === false && initialState.orderIdEnabled === false) {
      setOrderIdEnabled(true);
    }
  }, []);

  const handleSave = () => {
    saveTableBilling({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
    });

    // Reset state after saving
    setInitialState({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
      productEditEnabled,
    });
  };

  useEffect(() => {
    setInitialState({
      tableRows,
      billNoEnabled,
      billNo,
      deliveryFeeEnabled,
      orderIdEnabled,
      businessName,
      parcelFeeEnabled,
      tipsEnabled,
      productEditEnabled,
    });
  }, [user]);

  // Check if any of billNoEnabled or orderIdEnabled is true
  const isAnyFeatureEnabled = billNoEnabled || orderIdEnabled;

  const isChanged =
    tableRows !== initialState.tableRows ||
    billNoEnabled !== initialState.billNoEnabled ||
    billNo !== initialState.billNo ||
    deliveryFeeEnabled !== initialState.deliveryFeeEnabled ||
    orderIdEnabled !== initialState.orderIdEnabled ||
    businessName !== initialState.businessName ||
    parcelFeeEnabled !== initialState.parcelFeeEnabled ||
    tipsEnabled !== initialState.tipsEnabled ||
    productEditEnabled !== initialState.productEditEnabled;

  // Button should be enabled only if there are changes AND at least one feature (bill number or order ID) is enabled
  const isSaveButtonEnabled = isChanged && isAnyFeatureEnabled;

  useEffect(() => {
    if (user?.businessType !== "Grocery") {
      document.title = "TableBilling› Configuration › iMetanic";
    }
  }, []);

  const checkOrderBillNo = orderIdEnabled && billNoEnabled;

  return (
    <div className="w-full space-y-6 p-4 overflow-y-auto">
      <PageTitle title=" - Configuration › TableBilling" />
      <div className="border p-6 rounded-xl shadow bg-white space-y-6">
        {/* ORDER / BILL CONFIG */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Order ID / Bill Number & Delivery Fee Configuration</h2>

          {/* Show Order ID */}
          <label className="flex items-start gap-2 cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={orderIdEnabled}
              onChange={() => setOrderIdEnabled(!orderIdEnabled)}
              className="w-4 h-4 mt-1 accent-blue-600"
              disabled={!checkOrderBillNo && orderIdEnabled} // Disable if bill number is not enabled
            />
            <div>
              <span className="font-medium">Show Order ID</span>
              {!orderIdEnabled && <p className="text-xs text-gray-500">Enable order ID to show order IDs on bills and receipts</p>}
            </div>
          </label>
          <div className="border-t"></div>

          {/* Bill Reset */}
          <label className="flex items-start gap-2 cursor-pointer mb-4 mt-4">
            <input
              type="checkbox"
              checked={billNoEnabled}
              onChange={() => setBillNoEnabled(!billNoEnabled)}
              className="w-4 h-4 mt-1"
              disabled={!checkOrderBillNo && billNoEnabled} // Disable if order ID is not enabled
            />
            <div>
              <span className="font-medium">Enable Bill Number </span>
              {!billNoEnabled && <p className="text-xs text-gray-500">Enable bill number reset to select a reset period</p>}
            </div>
          </label>

          {/* Dropdown */}
          <div className={`${!billNoEnabled ? "opacity-50 pointer-events-none" : ""}`}>
            <label className="block text-sm font-medium mb-2">
              Bill Number Reset Period
              <span className="text-gray-400 ml-2">(No reset / Day / Week / Month / Year)</span>
            </label>

            <div className="max-w-xs">
              <DropdownSelect titleofSelect="Bill No Reset Period" name="billNo" value={billNo} defultSelect options={billNoDropDownValue} onChange={(e) => setBillNo(e as any)} />
            </div>
          </div>

          {/* Validation */}
          {!isAnyFeatureEnabled && <p className="text-xs text-red-500 mt-4 font-medium">❌ At least one feature (Bill Number Reset or Order ID) must be enabled</p>}
        </div>

        {/* DIVIDER */}
        <div className="border-t"></div>

        {user?.businessType === "Laundry" && (
          <>
            <div>
              <h2 className="text-lg font-semibold mb-4">Product Edit Enabled</h2>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={productEditEnabled} onChange={() => setProductEditEnabled(!productEditEnabled)} className="w-4 h-4 mt-1" />
                <div>
                  <span className="font-medium">Enable Product Edit</span>
                  {!productEditEnabled && <p className="text-xs text-gray-500">Enable product edit to allow editing product details in the cart</p>}
                </div>
              </label>
            </div>
            <div className="border-t"></div>
          </>
        )}

        {/* DELIVERY FEE
        <div>
          <h2 className="text-lg font-semibold mb-4">Delivery Fee</h2>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={deliveryFeeEnabled} onChange={() => setDeliveryFeeEnabled(!deliveryFeeEnabled)} className="w-4 h-4 mt-1" />
            <div>
              <span className="font-medium">Enable Delivery Fee</span>
              {!deliveryFeeEnabled && <p className="text-xs text-gray-500">Enable delivery fee to allow adding delivery charges to orders</p>}
            </div>
          </label>
        </div> */}
        {/* <div className="border-t"></div> */}

        {user?.businessType === "Restaurant" && (
          <>
            <div>
              <h2 className="text-lg font-semibold mb-4">Parcel Fee</h2>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={parcelFeeEnabled} onChange={() => setParcelFeeEnabled(!parcelFeeEnabled)} className="w-4 h-4 mt-1" />
                <div>
                  <span className="font-medium">Enable Parcel Fee</span>
                  {!parcelFeeEnabled && <p className="text-xs text-gray-500">Enable parcel fee in printer</p>}
                </div>
              </label>
            </div>
            <div className="border-t"></div>
          </>
        )}

        {user?.businessType === "Restaurant" && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Tips</h2>

            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={tipsEnabled} onChange={() => setTipsEnabled(!tipsEnabled)} className="w-4 h-4 mt-1" />

              <div>
                <span className="font-medium">Enable Tips</span>

                {!tipsEnabled && <p className="text-xs text-gray-500">Enable tips in printer</p>}
              </div>
            </label>
          </div>
        )}
      </div>
      {/* Table Configuration */}
      <div className="border p-6 rounded-xl shadow bg-white">
        <h2 className="text-lg font-semibold mb-4">Table Configuration</h2>

        <div>
          <label className="block text-sm font-medium mb-2">
            Table Data Shown (Rows)
            <span className="text-gray-400 ml-2">(10 / 25 / 50 / 100 / 250 / 500)</span>
          </label>
          <div className="max-w-xs">
            <DropdownSelect name="tableRows" titleofSelect="Table Data Shown" value={String(tableRows)} options={dropDownValue} onChange={(value) => setTableRows(Number(value))} />
          </div>
        </div>
      </div>

      {/* Custom Business Name - Grocery Only */}
      {user?.businessType === "Grocery" && (
        <div className="border p-6 rounded-xl shadow bg-white">
          <h2 className="text-lg font-semibold mb-4">Custom Business Name</h2>

          <div className="max-w-xs">
            <label className="block text-sm font-medium mb-2">Business Name</label>
            <InputBox placeholder="Enter business name" onChange={(e) => setBusinessName(e.target.value)} value={businessName} />
          </div>
        </div>
      )}
      {/* Save Button */}
      <div className="flex justify-center">
        <Button label="Save Table & Billing Settings" onClick={handleSave} disabled={!isSaveButtonEnabled} />
      </div>

      {/* Show message when button is disabled due to no feature enabled */}
      {!isAnyFeatureEnabled && isChanged && <div className="text-center text-sm text-red-500 mt-2">Please enable either "Bill Number Reset" or "Order ID" to save changes</div>}
    </div>
  );
};

export default TableBillingTab;
